import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import blogPosts from "../data/blogPosts";

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

// Lightbox component – fixed useEffect deps and missing import
const Lightbox = ({ images, currentIndex, onClose }) => {
  const [index, setIndex] = useState(currentIndex);

  const goPrev = useCallback(() => {
    setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goNext = useCallback(() => {
    setIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    },
    [onClose, goPrev, goNext],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="lightbox" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button className="lightbox-close" onClick={onClose}>
          ×
        </button>
        <button className="lightbox-prev" onClick={goPrev}>
          ‹
        </button>
        <img src={images[index]} alt="" />
        <button className="lightbox-next" onClick={goNext}>
          ›
        </button>
        <div className="lightbox-counter">
          {index + 1} / {images.length}
        </div>
      </div>
    </div>
  );
};

// Blog listing component
const BlogList = ({ posts, filter, setFilter, searchTerm, setSearchTerm }) => {
  const categories = ["all", ...new Set(posts.map((p) => p.category))];

  const filtered = posts.filter((p) => {
    const matchCat = filter === "all" || p.category === filter;
    const matchSearch = p.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <>
      <div className="blog-controls">
        <input
          type="text"
          placeholder="Search posts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="blog-filter">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`btn-filter ${filter === cat ? "active" : ""}`}
              onClick={() => setFilter(cat)}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="blog-grid">
        {filtered.length === 0 ? (
          <p>No posts found.</p>
        ) : (
          filtered.map((post) => (
            <article className="blog-card" key={post.id}>
              {post.images?.[0] && <img src={post.images[0]} alt="" />}
              <div className="blog-card-content">
                <span className="blog-date">{formatDate(post.date)}</span>
                <h2>
                  <Link to={`/blog/${post.id}`}>{post.title}</Link>
                </h2>
                <p>
                  {post.content.replace(/<[^>]*>/g, "").substring(0, 120)}...
                </p>
              </div>
            </article>
          ))
        )}
      </div>
    </>
  );
};

// Single blog post view
const BlogPost = ({ post, onBack }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const relatedPosts = blogPosts
    .filter((p) => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  return (
    <>
      <div className="blog-post-header">
        <Link to="/blog" onClick={onBack}>
          ← Back to Blog
        </Link>
        <h1>{post.title}</h1>
        <div className="blog-meta">
          <span>{formatDate(post.date)}</span>
          <span>{post.time}</span>
          <span>{post.category}</span>
          <span>By {post.author}</span>
        </div>
      </div>

      <div className="blog-post-layout">
        <article className="blog-post-content">
          {post.images?.length > 0 && (
            <div className="blog-gallery">
              {post.images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setLightboxIndex(idx);
                    setLightboxOpen(true);
                  }}
                >
                  <img src={img} alt="" />
                </div>
              ))}
            </div>
          )}
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        </article>

        {relatedPosts.length > 0 && (
          <aside className="blog-sidebar">
            <h3>Related Posts</h3>
            <ul>
              {relatedPosts.map((rp) => (
                <li key={rp.id}>
                  <Link to={`/blog/${rp.id}`}>
                    {rp.images?.[0] && <img src={rp.images[0]} alt="" />}
                    <div>
                      <h4>{rp.title}</h4>
                      <span>{formatDate(rp.date)}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>

      {lightboxOpen && (
        <Lightbox
          images={post.images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

// Main Blog component
const Blog = () => {
  const { id } = useParams();
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const post = id ? blogPosts.find((p) => p.id === parseInt(id)) : null;

  return (
    <>
      <section
        className="page-hero"
        style={{ backgroundImage: "url('/images/hero.jpg')" }}
      >
        <div className="container">
          <h1>Blog</h1>
        </div>
      </section>

      <section className="section container">
        {post ? (
          <BlogPost post={post} onBack={() => window.history.back()} />
        ) : (
          <BlogList
            posts={blogPosts}
            filter={filter}
            setFilter={setFilter}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        )}
      </section>
    </>
  );
};

export default Blog;
